"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
} from "@mui/material";
import {
  Schedule,
  CheckCircle,
  RocketLaunch,
  DesignServices,
  Build,
  AutoFixHigh,
  Support,
  Speed,
  Security,
  TrendingUp,
  Groups,
  ShoppingCart,
  Payment,
  Inventory,
} from "@mui/icons-material";
import Head from "next/head";
import PopoverForm from "@/components/PopoverForm";

export default function Ecommerce() {
  const theme = useTheme();

  const painPoints = [
    {
      title: "Low Conversion Rates",
      description:
        "Visitors not turning into customers due to poor user experience and slow performance.",
    },
    {
      title: "Manual Order Processing",
      description:
        "Time-consuming manual order fulfillment, inventory updates, and customer communications.",
    },
    {
      title: "High Cart Abandonment",
      description:
        "Customers adding items to cart but not completing purchases due to friction in checkout.",
    },
    {
      title: "Poor Customer Support",
      description:
        "Slow response times and inconsistent customer service affecting brand reputation.",
    },
  ];

  const solutions = [
    {
      title: "High-Performance Stores",
      description:
        "Fast, secure eCommerce websites built with Next.js, Shopify, WooCommerce, and custom solutions.",
    },
    {
      title: "AI & Workflow Automation",
      description:
        "Automated inventory sync, email flows, abandoned cart recovery, and order processing.",
    },
    {
      title: "Secure Payment Integration",
      description:
        "Seamless payment gateways with multiple currency support and fraud protection.",
    },
    {
      title: "Automated Reporting",
      description:
        "Real-time dashboards for sales, inventory, customer behavior, and business performance.",
    },
  ];

  const keyServices = [
    {
      title: "Custom eCommerce Development",
      description:
        "Tailored online stores built for performance and scalability",
      features: [
        "Next.js/React Stores",
        "Shopify Development",
        "WooCommerce",
        "Custom Platforms",
      ],
      icon: <Build sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Automation & Integration",
      description: "Streamline operations with intelligent automation",
      features: [
        "Zapier/Make Integration",
        "AI Chatbots",
        "Marketing Automation",
        "Inventory Sync",
      ],
      icon: <AutoFixHigh sx={{ fontSize: 40 }} />,
      color: theme.palette.secondary.main,
    },
    {
      title: "Performance Optimization",
      description: "Speed, SEO, and conversion rate optimization",
      features: [
        "Speed Optimization",
        "SEO Enhancement",
        "Checkout Optimization",
        "Mobile-First Design",
      ],
      icon: <Speed sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Support & Security",
      description: "Reliable ongoing maintenance and protection",
      features: [
        "24/7 Monitoring",
        "Security Updates",
        "Payment Security",
        "Data Protection",
      ],
      icon: <Security sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
    },
  ];

  const processSteps = [
    {
      label: "Strategy & Requirements",
      description:
        "Analyze your business model, target audience, and technical requirements to create a winning eCommerce strategy.",
      icon: <Inventory />,
    },
    {
      label: "Design & Development",
      description:
        "Create compelling user experiences and build high-performance, scalable eCommerce architecture.",
      icon: <DesignServices />,
    },
    {
      label: "Automation Integration",
      description:
        "Implement intelligent automation for inventory, orders, marketing, and customer service.",
      icon: <AutoFixHigh />,
    },
    {
      label: "Testing & Launch",
      description:
        "Comprehensive testing, security audits, and smooth deployment to live environment.",
      icon: <RocketLaunch />,
    },
    {
      label: "Ongoing Support & Scaling",
      description:
        "Continuous optimization, feature updates, and scaling as your business grows.",
      icon: <Support />,
    },
  ];

  const whyChoosePoints = [
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: "eCommerce Growth Experts",
      description:
        "Proven track record in driving sales and improving conversion rates for online stores.",
    },
    {
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      title: "Multi-Platform Experience",
      description:
        "Expertise across Shopify, WooCommerce, Next.js custom stores, and enterprise platforms.",
    },
    {
      icon: <Groups sx={{ fontSize: 40 }} />,
      title: "Global-First Delivery",
      description:
        "Serving eCommerce businesses in US, UK, Netherlands, Germany, UAE, and worldwide.",
    },
    {
      icon: <Payment sx={{ fontSize: 40 }} />,
      title: "High Impact, Affordable",
      description:
        "Enterprise-grade solutions at competitive prices, delivering measurable ROI for your store.",
    },
  ];

  const targetMarkets = [
    {
      country: "United States",
      flag: "🇺🇸",
      description: "Largest eCommerce market",
    },
    {
      country: "United Kingdom",
      flag: "🇬🇧",
      description: "Advanced digital consumers",
    },
    {
      country: "Netherlands",
      flag: "🇳🇱",
      description: "High online penetration",
    },
    {
      country: "Germany",
      flag: "🇩🇪",
      description: "Strong manufacturing base",
    },
    { country: "UAE", flag: "🇦🇪", description: "Rapid digital transformation" },
    { country: "Global", flag: "🌍", description: "Cross-border eCommerce" },
  ];

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  return (
    <>
      <Head>
        <title>eCommerce Development & Automation | LinXar Labs</title>
        <meta
          name="description"
          content="LinXar Labs helps eCommerce businesses build high-performing online stores, automate workflows, and increase sales. Serving clients globally."
        />
        <meta
          name="keywords"
          content="eCommerce development, Shopify developers, WooCommerce experts, custom online store development, eCommerce automation, abandoned cart recovery, automated inventory sync, global eCommerce solutions"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: "eCommerce Development & Automation",
              provider: {
                "@type": "Organization",
                name: "LinXar Labs",
                url: "https://linxarlabs.com",
              },
              areaServed: [
                { "@type": "Country", name: "United States" },
                { "@type": "Country", name: "United Kingdom" },
                { "@type": "Country", name: "Netherlands" },
                { "@type": "Country", name: "Germany" },
                { "@type": "Country", name: "UAE" },
              ],
              description:
                "LinXar Labs builds high-performing eCommerce websites and automates operations for brands worldwide.",
            }),
          }}
        />
      </Head>

      <Box sx={{ minHeight: "100vh", background: "white" }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: "white",
            py: { xs: 8, md: 12 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    fontWeight: 700,
                    lineHeight: 1.2,
                    mb: 3,
                  }}
                >
                  eCommerce Development & Automation That Drives Sales
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    lineHeight: 1.6,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                  }}
                >
                  We help eCommerce brands build fast, secure stores and
                  automate operations for growth and efficiency.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Schedule />}
                    onClick={handleConsult}
                    sx={{
                      bgcolor: "white",
                      color: theme.palette.primary.main,
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: alpha("#fff", 0.9),
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Book a Free Consultation
                  </Button>
                  {/* <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: "white",
                      color: "white",
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "white",
                        bgcolor: alpha("#fff", 0.1),
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    View eCommerce Portfolio
                  </Button> */}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    background: alpha("#fff", 0.1),
                    borderRadius: 4,
                    p: 4,
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    🛍️ eCommerce Specialists
                  </Typography>
                  <List>
                    {[
                      "Shopify & WooCommerce experts",
                      "Custom store development",
                      "Payment gateway integration",
                      "Multi-currency support",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ color: "white" }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Pain Points vs Solutions Section */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={6}>
              {/* Pain Points Column */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    color: theme.palette.error.main,
                  }}
                >
                  Challenges for eCommerce Businesses
                </Typography>
                <Box sx={{ mt: 4 }}>
                  {painPoints.map((point, index) => (
                    <Card
                      key={index}
                      sx={{
                        mb: 3,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                        background: alpha(theme.palette.error.main, 0.02),
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              color: theme.palette.error.main,
                              flexShrink: 0,
                              mt: 0.5,
                            }}
                          >
                            <WarningIcon />
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.error.main,
                              }}
                            >
                              {point.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {point.description}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Grid>

              {/* Solutions Column */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    color: theme.palette.success.dark,
                  }}
                >
                  Our Solutions
                </Typography>
                <Box sx={{ mt: 4 }}>
                  {solutions.map((solution, index) => (
                    <Card
                      key={index}
                      sx={{
                        mb: 3,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        background: alpha(theme.palette.success.main, 0.03),
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateX(8px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              color: theme.palette.success.dark,
                              flexShrink: 0,
                              mt: 0.5,
                            }}
                          >
                            <CheckCircle />
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.success.dark,
                              }}
                            >
                              {solution.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {solution.description}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Target Markets Section */}
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            background: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 4,
              }}
            >
              Serving eCommerce Businesses Worldwide
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {targetMarkets.map((market, index) => (
                <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      background: "white",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <Typography variant="h4" sx={{ mb: 1, fontSize: "2rem" }}>
                      {market.flag}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      {market.country}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {market.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Key Services Section */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "3rem" },
                mb: 2,
              }}
            >
              Key Services for eCommerce
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
            >
              Comprehensive solutions tailored for online retail success and
              operational efficiency
            </Typography>

            <Grid container spacing={4}>
              {keyServices.map((service, index) => (
                <Grid size={{ xs: 12, md: 6 }} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      border: `1px solid ${alpha(service.color, 0.2)}`,
                      background: "white",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 20px 40px ${alpha(service.color, 0.15)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <Box
                        sx={{
                          color: service.color,
                          mb: 3,
                        }}
                      >
                        {service.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ fontWeight: 600, color: service.color }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        paragraph
                      >
                        {service.description}
                      </Typography>
                      <Box sx={{ textAlign: "left" }}>
                        {service.features.map((feature, featureIndex) => (
                          <Typography
                            key={featureIndex}
                            variant="body1"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.8,
                              color: "text.primary",
                              fontWeight: 500,
                              "&:before": {
                                content: '"▸"',
                                color: service.color,
                                fontWeight: "bold",
                                mr: 1.5,
                              },
                            }}
                          >
                            {feature}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Case Study Highlight */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                  }}
                >
                  E-commerce Transformation
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Client: E-commerce SME in Ghana
                </Typography>

                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    Challenge
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Outdated WordPress site with poor SEO, slow performance, and
                    declining conversions.
                  </Typography>

                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    Solution
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Migrated to Next.js, optimized checkout flow, and integrated
                    real-time inventory management.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    background: alpha(theme.palette.success.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    borderRadius: 2,
                    p: 3,
                  }}
                >
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{ fontWeight: 700, color: theme.palette.success.main }}
                  >
                    Results
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={6}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        +65%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Faster Load Times
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        +40%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Increase in Conversions
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: "white",
                    p: 4,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{ fontWeight: 700 }}
                  >
                    Ready to See Similar Results?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                    Let&apos;s discuss how we can transform your web presence
                    and drive growth.
                  </Typography>
                  <PopoverForm
                    triggerElement={
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          bgcolor: "white",
                          color: theme.palette.primary.main,
                          px: 4,
                          py: 1.5,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: alpha("#fff", 0.9),
                          },
                        }}
                      >
                        Start Your Project
                      </Button>
                    }
                  />
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Process Section */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "3rem" },
                mb: 2,
              }}
            >
              Our eCommerce Development Process
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
            >
              A proven methodology for building and scaling successful online
              stores
            </Typography>

            <Stepper orientation="vertical" sx={{ maxWidth: 800, mx: "auto" }}>
              {processSteps.map((step) => (
                <Step key={step.label} active={true}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: "white",
                          width: 40,
                          height: 40,
                        }}
                      >
                        {step.icon}
                      </Avatar>
                    )}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mt: 1, lineHeight: 1.7 }}
                    >
                      {step.description}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Container>
        </Box>

        {/* Why LinXar Labs Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            background: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "3rem" },
                mb: 2,
              }}
            >
              Why Choose LinXar Labs for eCommerce?
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 8, maxWidth: 600, mx: "auto" }}
            >
              We combine eCommerce expertise with technical excellence to drive
              your online success
            </Typography>

            <Grid container spacing={4}>
              {whyChoosePoints.map((point, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      background: "white",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.12)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 3,
                        }}
                      >
                        <Box
                          sx={{
                            color: theme.palette.primary.main,
                            flexShrink: 0,
                          }}
                        >
                          {point.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h5"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {point.title}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {point.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Final CTA Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            py: { xs: 8, md: 12 },
            textAlign: "center",
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "3rem" },
                mb: 3,
              }}
            >
              Let&apos;s Build Your eCommerce Growth Engine
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 6, lineHeight: 1.6 }}
            >
              Ready to transform your online store with high-performance
              development and intelligent automation?
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Schedule />}
              onClick={handleConsult}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                },
                transition: "all 0.3s ease",
              }}
            >
              Book a Free Strategy Call
            </Button>
          </Container>
        </Box>
      </Box>
    </>
  );
}

// Add the missing WarningIcon component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function WarningIcon(props: any) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="currentColor">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}
